<template>
	<tr class="data-row" @click="toggleExpand">
		<td>{{ payoff.id }}</td>
		<td>{{ payoff.batchId }}</td>
		<td>{{ payoff.disbursementStatusHumanized }}</td>
		<td>{{ payoff.lenderName }}</td>
		<td>{{ payoff.servicerName }}</td>
		<td>{{ payoff.accountNumber }}</td>
		<td>{{ payoff.transactionDateFormatted }}</td>
		<td>{{ payoff.disbursementAmountFormatted }}</td>
		<td>{{ payoff.adjustedDisbursementAmountFormatted }}</td>
		<td>{{ payoff.disbursementTypeHumanized }}</td>
		<td>{{ payoff.settleDateFormatted }}</td>
	</tr>
	<!-- todo: make child content responsive? -->
	<tr v-if="isExpanded" class="expanded-row" :class="{'border-bottom-none': showPostDisbursementChangesSection}">
		<td class="expanded-data" :colspan="12">
			<div class="expanded-wrapper">
				<PermissionsWrapper
					:permissionIds="[Permissions.Disbursements]"
					:permissionLevel="PermissionLevels.Update"
				>
					<div class="action-button-container">
						<button :disabled="mode === 'edit'" @click="changeMode('edit')">Edit</button>
						<button v-show="mode !== 'view'" :disabled="isSubmitting" @click="changeMode('view')">
							Cancel
						</button>
					</div>
				</PermissionsWrapper>
				<PayoffForm
					:isSubmitting="isSubmitting"
					:mode="mode"
					:fields="formFields"
					:validationSchema="validationSchema"
					:originalDisbursementAmount="payoff.adjustedDisbursementAmount"
					@submit="updatePayoffAsync"
				/>
				<ServicerMailingInformation
					:subServicerName="payoff.subServicerName"
					:address="payoff.subServicerAddress"
				/>
				<PaymentDetails :financialInstitution="payoff.financialInstitution" />
			</div>
		</td>
	</tr>
  <tr
    v-if="showPostDisbursementChangesSection"
    class="expanded-row post-disbursement-changes-section"
  >
    <td :colspan="12">
      <PostDisbursementChangesTable :postDisbursementChanges="postDisbursementChanges" />
    </td>
  </tr>
</template>

<script setup lang="ts">
	import { ref, computed } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import { PayoffDetails, PayoffPostDisbursementChange, PayoffPostDisbursementChangeModel } from '@/models/loans';
	import PayoffForm from '@/components/loan/refi/PayoffForm.vue';
	import { usePayoffData } from '@/composables/loan/refi/usePayoffData';
	import { useFormFields } from '@/composables/loan/refi/usePayoffForm';
	import PermissionsWrapper from '@/components/wrappers/PermissionsWrapper.vue';
	import { Permissions, PermissionLevels } from '@/models/permissions';
	import ServicerMailingInformation from './ServicerMailingInformation.vue';
	import PaymentDetails from './PaymentDetails.vue';
	import PostDisbursementChangesTable from './PostDisbursementChangesTable.vue';

	const props = defineProps<{
		payoff: PayoffDetails;
		onPayoffUpdatedAsync: () => Promise<void>;
	}>();

	// todo: expand from query?

	const payoff = computed<PayoffDetails>(() => props.payoff);

	const postDisbursementChanges = computed<Array<PayoffPostDisbursementChangeModel>>(() => payoff.value?.payoffPostDisbursementChanges);

	const showPostDisbursementChangesSection = computed<boolean>(() => isExpanded.value && postDisbursementChanges.value?.length > 0);

	const isExpanded = ref(false);
	const isSubmitting = ref<boolean>(false);
	const mode = ref<'view' | 'edit'>('view');

	const { payoffDisbursementStatusOptions, payoffDisbursementStatusReasonOptions, payoffDisbursementTypeOptions } =
		usePayoffData();

	const { formFields, validationSchema } = useFormFields({
		mode,
		payoff,
		payoffDisbursementStatusOptions,
		payoffDisbursementStatusReasonOptions,
		payoffDisbursementTypeOptions
	});

	const toggleExpand = () => (isExpanded.value = !isExpanded.value);

	const changeMode = (newMode: 'view' | 'edit') => (mode.value = newMode);

	async function updatePayoffAsync(
		formData: Record<string, any>,
		postDisbursementChange: PayoffPostDisbursementChange | null
	) {
		const payoffId = payoff.value.id;

		isSubmitting.value = true;

		// set to unadjusted amounts to preserve on patch
		formData['disbursementAmount'] = payoff.value.disbursementAmount;
		formData['payoffFees'] = payoff.value.payoffFees;

		const request = {
			id: payoffId,
			postDisbursementChange,
			...formData
		};

		functions.openModal({
			title: 'Confirm',
			description: `Are you sure you want to update payoff ${payoffId}?`,
			buttons: [
				{ title: 'Cancel', onClick: () => functions.closeModal() },
				{
					title: 'Save Changes',
					onClick: async () => {
						try {
							await axios.patch(`/api/loanpayoffs/${payoffId}`, request);
							await props.onPayoffUpdatedAsync();

							changeMode('view');

							functions.openModal({
								title: 'Success!',
								description: `Payoff ${payoffId} updated successfully!`
							});
						} catch (error: any) {
							let errorMessage = 'There was an issue updating the payoff. Please try again later.';

							if (error.response?.data?.errors) {
								const errorMessages: any[] = Object.values(error.response?.data?.errors).flat();

								if (errorMessages.length > 0) {
									errorMessage = errorMessages.join(' ');
								}
							}

							functions.openModal({
								title: 'Oops!',
								description: errorMessage
							});
						} finally {
							isSubmitting.value = false;
						}
					}
				}
			]
		});
	}
</script>

<style scoped>
	td {
		padding: 10px 5px;
	}

	tr.data-row {
		text-align: center;
		text-wrap: nowrap;
		cursor: pointer;
		user-select: none;
	}

	tr:hover {
		background-color: #f2f2f2;
		transition: background-color 0.2s;
	}

	.expanded-row {
		border-bottom: 1px solid #ccc;
		overflow: hidden;
	}
	
	.expanded-row.post-disbursement-changes-section {
		background-color: #fff;
		padding-top: 0;
	}

	.expanded-row.post-disbursement-changes-section td {
		padding-top: 0;
	}
	 
	.expanded-row.border-bottom-none {
		border-bottom: none;
	}

	.expanded-row:hover {
		background-color: inherit;
		transition: none;
	}

	.expanded-data {
		padding: 0;
	}

	.expanded-wrapper {
		padding: 15px 15px;
		background-color: #f9f9f9;
	}

	.action-button-container {
		display: flex;
		justify-content: flex-end;
		gap: 10px;
		margin-bottom: 10px;
	}

	@media (max-width: 560px) {
		.action-button-container {
			justify-content: center;
			flex-wrap: wrap-reverse;
		}

		.select-buttons-container {
			justify-content: center;
		}

		button,
		.permission-wrapper,
		.dashed {
			width: 100% !important;
		}
	}

	button {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 8px 16px;
		background-color: black;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-family: 'Montserrat', Arial, sans-serif;
		transition: background-color 0.2s, color 0.2s;
		font-size: 14px;
		gap: 8px;
		min-width: 98px;
	}

	button:hover {
		background-color: #333;
	}

	button:disabled {
		background-color: #ccc;
		color: #666;
		cursor: not-allowed;
		opacity: 0.7;
	}
</style>
